// @vendors
import React, { useEffect, useState } from 'react'
import Highcharts, { SeriesOptionsType } from 'highcharts'
import { CircularProgress } from '@material-ui/core'
import moment from 'moment'
import 'moment/locale/es'

// @services
import {
    recaudosConceptosDiarias,
    recaudosConceptosMes
} from '../../app/vistas/Dashboard/servicios'

// @charts
import { FiltrosFecha } from 'app/vistas/Dashboard/Charts/Components'

// @storage
import { getAllConceptos } from 'app/vistas/GNV/GestionRecaudosGnv/services/gestionRecaudosGnv.service'


interface ConceptosRecuado {
    total_recaudado: number
    concepto: string
    fecha: number
}

// REPORTE DE CONCEPTO DE RECAUDOS
interface ReporteDiaConceptosRecaudos {
    fecha: string
    conceptos: ConceptosRecuado[]
}

//REPORTE DE GALONAJE POR UN RANGO DE FECHA (ESTE MES)
interface ReporteRangoConceptosRecaudos {
    fecha_inicial: string
    fecha_final: string
    conceptos: ConceptosRecuado[]
}

enum TIPO_REPORTE {
    HOY = 0,
    SEMANA = 1,
    MES = 2
}

const colores: any = {
    VBG_SOLUCIONES: 'red',
    ASIST: 'blue',
    ADM_DE_TRANSPORTISTAS: 'yellow',
}


const CharConceptosRecaudos = ({ updateData, identificadorEstacion, identificadorOrganizacion }: any) => {
    const [cargandoCombustibles, setCargandoCombustibles] = useState(true)
    const [cargandoReportes, setCargandoReportes] = useState(true)
    const [tipoReporte, setTipoReporte] = useState<TIPO_REPORTE>(TIPO_REPORTE.HOY)
    const [reportes, setReportes] = useState<ReporteDiaConceptosRecaudos[] | ReporteRangoConceptosRecaudos[]>([])
    const [conceptos, setConceptos] = useState<any[]>([])
    const [señalPeticion, setSeñalPeticion] = useState<AbortController | null>(null)

    const formatearDatos = (reportes: ReporteDiaConceptosRecaudos[] | ReporteRangoConceptosRecaudos[]): SeriesOptionsType[] => {
        return conceptos?.map(
            concepto =>
            ({
                name: concepto.concepto,
                color: colores[concepto.concepto?.split(' ').join('_')],
                data: reportes.map(reporte => {
                    const venta = reporte.conceptos?.find(venta => venta.concepto === concepto.concepto)
                    return venta ? venta.total_recaudado : 0
                })
            } as SeriesOptionsType)
        )
    }

    const cargarReportes = async () => {
        try {
            const señalPeticionActual = new AbortController()
            const fechaActual = moment()
            let res = null

            setSeñalPeticion(señalPeticionActual)

            switch (tipoReporte) {
                case TIPO_REPORTE.HOY:
                    res = await recaudosConceptosDiarias(
                        Number(identificadorEstacion),
                        fechaActual.startOf('day').format(),
                        fechaActual.endOf('day').format(),
                        señalPeticionActual
                    )
                    break
                case TIPO_REPORTE.SEMANA:
                    res = await recaudosConceptosDiarias(
                        Number(identificadorEstacion),
                        fechaActual.startOf('week').format(),
                        fechaActual.endOf('week').format(),
                        señalPeticionActual
                    )
                    break
                case TIPO_REPORTE.MES:
                    res = await recaudosConceptosMes(
                        Number(identificadorEstacion),
                        fechaActual.startOf('month').format(),
                        fechaActual.endOf('month').format(),
                        señalPeticionActual
                    )
                    break
            }
            if (res) setReportes(res.data.data as ReporteDiaConceptosRecaudos[])
        } catch (error) {
            console.error(error)
        } finally {
            setCargandoReportes(false)
        }
    }

    const graficoLabels = () => {
        switch (tipoReporte) {
            case TIPO_REPORTE.HOY:
                return ['Hoy']
            case TIPO_REPORTE.SEMANA:
                return (reportes as ReporteDiaConceptosRecaudos[]).map(reporte =>
                    moment(reporte.fecha)
                        .locale('es')
                        .format('ddd')
                )
            case TIPO_REPORTE.MES:
                return (reportes as ReporteRangoConceptosRecaudos[]).map(reporte => {
                    const fechaIncial = moment(reporte.fecha_inicial)
                        .locale('es')
                        .format('D')
                    const fechaFinal = moment(reporte.fecha_final)
                        .locale('es')
                        .format('D')
                    return `${fechaIncial} - ${fechaFinal}`
                })
        }
    }

    const crearGrafica = () => {
        Highcharts.chart('container__chartRecaudosConceptos', {
            chart: { type: 'column', animation: false },
            title: { text: '' },
            xAxis: { categories: graficoLabels(), crosshair: true },
            yAxis: { min: 0 },
            tooltip: {
                headerFormat: '<span style="font-size:10px">{point.key}</span><table>',
                pointFormat: `<tr>
                                <td style="color:{pickColor(series.color)};padding:0">{series.name}:</td>
                                <td style="padding:0"><b>$ {point.y:.2f}</b></td>
                            </tr>`,
                footerFormat: '</table>',
                shared: true,
                useHTML: true,
            },
            plotOptions: {
                column: {
                    pointPadding: 0.2,
                    borderWidth: 0
                }
            },
            series: formatearDatos(reportes)
        })
    }

    const cargarCombustibles = async () => {
        const { data } = await getAllConceptos(identificadorOrganizacion)

        setCargandoCombustibles(true)
        setConceptos(data.data ?? [])
        setCargandoCombustibles(false)
    }

    useEffect(() => { cargarCombustibles() }, [])

    useEffect(() => { cargarReportes() }, [updateData])

    useEffect(() => {
        if (señalPeticion) señalPeticion.abort()
        cargarReportes()
    }, [tipoReporte, identificadorEstacion])

    useEffect(() => {
        if (!cargandoReportes && !cargandoCombustibles) crearGrafica()
    }, [cargandoReportes, cargandoCombustibles, reportes])

    return (
        <div className="d-flex flex-column w-100 h-100">
            {cargandoReportes || cargandoCombustibles ? (
                <CircularProgress className="m-auto" />
            ) : (
                <div className="w-100 h-100" id="container__chartRecaudosConceptos"></div>
            )}
            <div className="mt-auto">
                <FiltrosFecha onChange={(tipo: TIPO_REPORTE) => setTipoReporte(tipo)} filtroOut={['ESTE AÑO']} />
            </div>
        </div>
    )
}

export default CharConceptosRecaudos

